import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    HouseholdReportingSettingsView,
    ReportingPreset,
    ReportingPresetView,
    ReportingSettingsOverrides,
    TenantReportingSettingsView,
} from '@ndvr/reporting-api';

import { ReportingApi } from 'API';
import { Toast } from 'widgets';

const requireResponse = <T>(response: T | null, message: string): T => {
    if (response === null) {
        throw new Error(message);
    }
    return response;
};

export const ReportingSettingsQueryKeys = {
    presets: ['reporting-settings', 'presets'] as const,
    tenant: (tenantGUID: string) => ['reporting-settings', 'tenant', tenantGUID] as const,
    household: (globalAccountGUID: string) => ['reporting-settings', 'household', globalAccountGUID] as const,
};

export const useReportingSettingsPresets = () =>
    useQuery<ReportingPresetView[]>({
        queryKey: ReportingSettingsQueryKeys.presets,
        queryFn: async () => {
            const [presets] = await ReportingApi().getReportingSettingsPresets();
            return requireResponse(presets, 'The reporting settings API returned no presets.');
        },
        staleTime: Infinity,
    });

export const useTenantReportingSettings = (tenantGUID: string) =>
    useQuery<TenantReportingSettingsView>({
        queryKey: ReportingSettingsQueryKeys.tenant(tenantGUID),
        enabled: !!tenantGUID,
        queryFn: async () => {
            const [settings] = await ReportingApi().getTenantReportingSettings(tenantGUID);
            return requireResponse(settings, 'The reporting settings API returned no tenant settings.');
        },
    });

export const useHouseholdReportingSettings = (globalAccountGUID: string) =>
    useQuery<HouseholdReportingSettingsView>({
        queryKey: ReportingSettingsQueryKeys.household(globalAccountGUID),
        enabled: !!globalAccountGUID,
        queryFn: async () => {
            const [settings] = await ReportingApi().getHouseholdReportingSettings(globalAccountGUID);
            return requireResponse(settings, 'The reporting settings API returned no household settings.');
        },
    });

export const useSaveTenantReportingSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            tenantGUID,
            preset,
            overrides,
        }: {
            tenantGUID: string;
            preset: ReportingPreset;
            overrides: ReportingSettingsOverrides;
        }) => {
            const [response] = await ReportingApi({ hideError: true }).replaceTenantReportingSettings(tenantGUID, {
                preset,
                overrides,
            });
            return requireResponse(response, 'The reporting settings API returned no tenant settings.');
        },
        onSuccess: (settings, variables) => {
            queryClient.setQueryData(ReportingSettingsQueryKeys.tenant(variables.tenantGUID), settings);
            Toast.success('Reporting settings saved.');
        },
        onError: (error, variables) => {
            void queryClient.invalidateQueries({ queryKey: ReportingSettingsQueryKeys.tenant(variables.tenantGUID) });
            Toast.forError(error, 'Failed to save reporting settings.');
        },
    });
};

export const useSaveHouseholdReportingSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            globalAccountGUID,
            overrides,
        }: {
            globalAccountGUID: string;
            overrides: ReportingSettingsOverrides;
        }) => {
            const [settings] = await ReportingApi({ hideError: true }).replaceHouseholdReportingSettingsOverrides(
                globalAccountGUID,
                overrides,
            );
            return requireResponse(settings, 'The reporting settings API returned no household settings.');
        },
        onSuccess: (settings, variables) => {
            queryClient.setQueryData(ReportingSettingsQueryKeys.household(variables.globalAccountGUID), settings);
            Toast.success('Household reporting settings saved.');
        },
        onError: (error) => Toast.forError(error, 'Failed to save household reporting settings.'),
    });
};

export const useResetHouseholdReportingSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (globalAccountGUID: string) => {
            const reportingApi = ReportingApi({ hideError: true });
            await reportingApi.clearHouseholdReportingSettingsOverrides(globalAccountGUID);
            const [settings] = await reportingApi.getHouseholdReportingSettings(globalAccountGUID);
            return requireResponse(settings, 'The reporting settings API returned no household settings.');
        },
        onSuccess: (settings, globalAccountGUID) => {
            queryClient.setQueryData(ReportingSettingsQueryKeys.household(globalAccountGUID), settings);
            Toast.success('Tenant reporting defaults restored.');
        },
        onError: (error) => Toast.forError(error, 'Failed to reset household reporting settings.'),
    });
};
